import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axiosInstance from "../config/axios";
import { loginSuccess, logout as logoutAction } from "../store/reducers/authReducer";

type Language = string;

interface User {
  id: string;
  name: string;
  email: string;
  role?: 'admin' | 'manager' | 'viewer' | 'hr' | 'employee'; // User role
  apps: string[]; // Array of app identifiers the user has access to (derived from applicationAccess)
  currentApp?: string;
  languagePreference?: Language | null;
}

interface CompanyLocaleSettings {
  primaryLanguage: Language;
  allowUserLanguageOverride: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
  hasAppAccess: (appName: string) => boolean;
  switchApp: (appName: string) => void;
  // Localization
  effectiveLanguage: Language;
  availableLanguages: Language[];
  company: CompanyLocaleSettings | null;
  setUserLanguage: (lang: Language | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Best-effort i18n integration. The shared package doesn't depend on i18next directly,
// so we look it up from window if the frontend has bootstrapped it.
function applyLanguageToI18n(lang: Language): void {
  const w = typeof window !== 'undefined' ? (window as any) : undefined;
  const i18n = w?.__APP_I18N__;
  if (i18n && typeof i18n.changeLanguage === 'function') {
    i18n.changeLanguage(lang).catch(() => {
      /* swallow — handler logs in dev */
    });
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [effectiveLanguage, setEffectiveLanguage] = useState<Language>('en');
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>(['en']);
  const [company, setCompany] = useState<CompanyLocaleSettings | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle returnUrl and token from URL params
    const token = searchParams.get("token");
    const returnUrl = searchParams.get("returnUrl");

    if (token) {
      localStorage.setItem("token", token);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Fetch user data
      checkAuth().then(() => {
        if (returnUrl) {
          navigate(returnUrl);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axiosInstance.get("/api/auth/me");
      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);

        // Apply localization payload from /auth/me
        if (response.data.effectiveLanguage) {
          setEffectiveLanguage(response.data.effectiveLanguage);
          applyLanguageToI18n(response.data.effectiveLanguage);
        }
        if (Array.isArray(response.data.availableLanguages)) {
          setAvailableLanguages(response.data.availableLanguages);
        }
        if (response.data.company) {
          setCompany({
            primaryLanguage: response.data.company.primaryLanguage,
            allowUserLanguageOverride: !!response.data.company.allowUserLanguageOverride,
          });
        }

        // Also update Redux store
        const reduxUser = {
          id: userData.id,
          email: userData.email,
          displayName: userData.name,
          firstName: userData.name?.split(' ')[0],
          lastName: userData.name?.split(' ')[1],
          role: userData.role || 'viewer'
        };
        dispatch(loginSuccess({ user: reduxUser, token }));
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
      dispatch(logoutAction());
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    const currentUrl = window.location.pathname;
    const returnUrl = encodeURIComponent(currentUrl);
    // Redirect to backend login page with return URL
    window.location.href = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/auth/login?returnUrl=${returnUrl}`;
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      setUser(null);
      dispatch(logoutAction());
      navigate("/");
    }
  };

  const hasAppAccess = (appName: string): boolean => {
    if (!user) return false;
    // Admin role has access to all apps
    if (user.role === 'admin') return true;
    // Case-insensitive comparison for app names
    const hasAccess = user.apps.some(app => app.toLowerCase() === appName.toLowerCase());
    return hasAccess;
  };

  const switchApp = (appName: string) => {
    if (hasAppAccess(appName)) {
      navigate(`/${appName}`);
    }
  };

  const setUserLanguage = async (lang: Language | null): Promise<void> => {
    const response = await axiosInstance.patch('/api/users/me/language', { language: lang });
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to update language');
    }
    const data = response.data.data || {};
    if (user) {
      setUser({ ...user, languagePreference: data.languagePreference ?? null });
    }
    if (data.effectiveLanguage) {
      setEffectiveLanguage(data.effectiveLanguage);
      applyLanguageToI18n(data.effectiveLanguage);
    }
    if (Array.isArray(data.availableLanguages)) {
      setAvailableLanguages(data.availableLanguages);
    }
    if (data.company) {
      setCompany({
        primaryLanguage: data.company.primaryLanguage,
        allowUserLanguageOverride: !!data.company.allowUserLanguageOverride,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        hasAppAccess,
        switchApp,
        effectiveLanguage,
        availableLanguages,
        company,
        setUserLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
